import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ArrowRight, MapPin, Users, Activity, Handshake,
  Building2, Heart, GraduationCap, Briefcase, Home as HomeIcon,
  Landmark, Compass, Target,
  CheckCircle2, XCircle, Clock, Repeat, Eye, ClipboardCheck, PhoneOff, TrendingUp,
} from "lucide-react";
import { CtaBanner } from "@/components/CTA";
import Seo from "@/components/Seo";
import PittsburghHero from "@/components/PittsburghHero";
import PilotBadge from "@/components/PilotBadge";
import ScanToConnect from "@/components/ScanToConnect";
import WhoGuidesDemo from "@/components/WhoGuidesDemo";
import Testimonials from "@/components/Testimonials";
import PartnerAppreciation from "@/components/PartnerAppreciation";

const HERO_IMG = "https://images.unsplash.com/photo-1758575514459-8a9b37d68047?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NjZ8MHwxfHNlYXJjaHwyfHxtaWxpdGFyeSUyMGZhbWlseSUyMGh1Z2dpbmd8ZW58MHx8fHwxNzgzNTYxNjE3fDA&ixlib=rb-4.1.0&q=85";
const COMMUNITY_IMG = "/assets/who-partners-table.jpg";
const MEETING_IMG = "https://images.unsplash.com/photo-1573164574511-73c773193279?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA3MDR8MHwxfHNlYXJjaHw0fHxkaXZlcnNlJTIwY29tbXVuaXR5JTIwbWVldGluZyUyMGRpc2N1c3Npb258ZW58MHx8fHwxNzgzNTYxNjE3fDA&ixlib=rb-4.1.0&q=85";

export default function Home() {
  return (
    <div data-testid="home-page">
      <Seo
        title="WHO — Warm Hand Off | Coordination Infrastructure for Veterans & Community"
        description="WHO is a coordination platform for veterans, families, and the organizations that serve them. WHO Guides™ — real people supported by AI — coordinate every warm handoff and stay with each person until the connection is made and the outcome is confirmed. Pittsburgh pilot now open to partners."
        path="/"
        structured
      />
      {/* PITTSBURGH HERO — problems swirling around the transitioning group */}
      <PittsburghHero />

      {/* WHAT IS A WARM HANDOFF? — plain-language explainer narrative */}
      <section
        className="relative py-24 lg:py-32 bg-white border-b border-borderSubtle overflow-hidden"
        data-testid="what-is-warm-handoff-section"
        id="what-is-a-warm-handoff"
      >
        <div className="absolute inset-0 pointer-events-none" aria-hidden>
          <div className="absolute -top-32 -right-32 w-[420px] h-[420px] rounded-full bg-gold/8 blur-3xl" />
          <div className="absolute -bottom-40 -left-40 w-[460px] h-[460px] rounded-full bg-navy/[0.03] blur-3xl" />
        </div>

        <div className="relative max-w-[1100px] mx-auto px-6 lg:px-10">
          <div className="grid lg:grid-cols-12 gap-12 items-center">
            {/* Copy column */}
            <div className="lg:col-span-7">
              <p className="eyebrow text-gold-deep" data-testid="warm-handoff-eyebrow">
                Plain language
              </p>
              <h2 className="mt-4 font-display font-black text-navy tracking-tight leading-[1.03] text-4xl sm:text-5xl lg:text-[3.4rem]">
                What is a <span className="text-gold-deep">warm handoff</span>?
              </h2>
              <div className="mt-5 h-[3px] w-16 bg-gold rounded-full" aria-hidden />

              <div className="mt-8 space-y-5 text-lg text-slateSoft leading-relaxed" data-testid="warm-handoff-copy">
                <p>
                  The term <span className="text-navy font-semibold">warm handoff</span> comes from
                  military and healthcare settings, where a person, patient, or responsibility is
                  transferred <span className="text-navy font-semibold">directly</span> from one
                  trusted individual or team to another.
                </p>
                <p className="text-navy font-medium">
                  In plain language, it means not leaving someone to navigate the next step alone.
                </p>
                <p>
                  Instead of simply providing a phone number, website, or list of resources, a warm
                  handoff makes the introduction, shares important information &mdash; with
                  permission &mdash; and helps confirm that the connection was successfully
                  completed.
                </p>
              </div>
            </div>

            {/* Featured statement callout */}
            <motion.aside
              initial={{ opacity: 0, y: 12 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.55 }}
              className="lg:col-span-5 relative rounded-2xl overflow-hidden p-7 lg:p-8 bg-navy-deep text-cream border border-gold/40"
              data-testid="warm-handoff-callout"
            >
              <div className="absolute inset-0 pointer-events-none" aria-hidden>
                <div className="absolute -top-24 -right-24 w-56 h-56 rounded-full bg-gold/12 blur-2xl" />
              </div>
              <div className="relative">
                <span className="font-display font-black text-gold text-6xl leading-none select-none" aria-hidden>
                  &ldquo;
                </span>
                <p className="mt-2 font-display font-bold text-cream text-xl lg:text-[1.4rem] leading-[1.3]">
                  It is the difference between saying,{" "}
                  <span className="text-cream/70">&ldquo;Here is where you can get help,&rdquo;</span>{" "}
                  and saying,{" "}
                  <span className="text-gold">&ldquo;Let&rsquo;s connect you to the right person together.&rdquo;</span>
                </p>
              </div>
            </motion.aside>
          </div>
        </div>
      </section>

      {/* FROM A WARM HANDOFF TO WHO — the bridge to the mission */}
      <section
        className="relative py-24 lg:py-32 bg-cream-panel border-b border-borderSubtle overflow-hidden"
        data-testid="handoff-to-who-section"
        id="from-warm-handoff-to-who"
      >
        <div className="absolute inset-0 pointer-events-none" aria-hidden>
          <div className="absolute -top-40 -right-40 w-[520px] h-[520px] rounded-full bg-gold/10 blur-3xl" />
          <div className="absolute -bottom-40 -left-40 w-[520px] h-[520px] rounded-full bg-navy/[0.03] blur-3xl" />
        </div>

        <div className="relative max-w-[1240px] mx-auto px-6 lg:px-10">
          {/* Narrative header */}
          <div className="max-w-[900px] mb-14">
            <p className="eyebrow text-gold-deep" data-testid="handoff-to-who-eyebrow">
              From a practice to a platform
            </p>
            <h2 className="mt-4 font-display font-black text-navy tracking-tight leading-[1.03] text-4xl sm:text-5xl lg:text-[3.4rem]">
              From a warm handoff <br className="hidden sm:block" />
              <span className="text-gold-deep">to WHO</span>.
            </h2>
            <div className="mt-5 h-[3px] w-16 bg-gold rounded-full" aria-hidden />

            <div className="mt-8 space-y-5 text-lg text-slateSoft leading-relaxed" data-testid="handoff-to-who-copy">
              <p>
                A warm handoff is a simple idea:{" "}
                <span className="text-navy font-semibold">
                  no one should be left to navigate the next step alone
                </span>.
              </p>
              <p>
                But across veteran services, healthcare, employment, housing, education, and
                benefits, that kind of connection is still too often dependent on phone calls,
                personal relationships, spreadsheets, and individual follow-up. One organization
                may make a referral, but there is often no shared way to know whether the
                connection was completed, whether the person received help, or what support may
                still be needed.
              </p>
              <p className="text-navy font-medium">
                WHO was created to bring the principles of a warm handoff into one coordinated
                system.
              </p>
              <p>
                WHO helps trusted organizations{" "}
                <span className="text-navy font-semibold">make supported connections</span>,
                communicate across service boundaries, track referrals, and follow through
                &mdash; so people are not simply pointed toward help, but{" "}
                <span className="text-navy font-semibold">connected to it</span>.
              </p>
              <p>
                What began as a trusted practice in military and healthcare settings becomes,
                through WHO, a more{" "}
                <span className="text-navy font-semibold">accountable, measurable, and
                human-centered</span> way to coordinate care and support.
              </p>
            </div>
          </div>

          <figure
            className="relative rounded-3xl bg-white border border-borderSubtle shadow-sm overflow-hidden"
            data-testid="handoff-to-who-figure"
          >
            <img
              src="/assets/practice-to-platform.png"
              alt="From a warm handoff to WHO — turning a trusted practice into a coordinated system. Panel 1: A warm handoff is a trusted person directly connecting someone to the next source of help. Panel 2: Without a shared system, follow-through depends on calls, spreadsheets, and individual effort across healthcare, housing, employment, education, and benefits. Panel 3: The WHO platform helps organizations connect, communicate, track referrals, and confirm support with supported connections, shared communication, referral tracking, and confirmed follow-through. Not just a referral. A connected, accountable handoff."
              className="block w-full h-auto"
              loading="lazy"
              data-testid="handoff-to-who-image"
            />
          </figure>
        </div>
      </section>

      {/* HERO */}
      <section className="relative overflow-hidden bg-white" data-testid="hero-section">
        <div className="absolute inset-0 dot-grid opacity-40 pointer-events-none" aria-hidden />
        <div className="relative max-w-[1240px] mx-auto px-6 lg:px-10 pt-16 lg:pt-24 pb-20 lg:pb-28 grid lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-7">
            <motion.div
              initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}
            >
              <PilotBadge />
            </motion.div>
            <motion.p
              initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.05, duration: 0.5 }}
              className="mt-4 eyebrow text-gold-deep" data-testid="hero-eyebrow"
            >
              Coordination Infrastructure &middot; Warm Hand Off
            </motion.p>
            <motion.h1
              initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1, duration: 0.6 }}
              className="mt-5 font-display font-black text-navy-900 tracking-tight leading-[0.98] text-5xl sm:text-6xl lg:text-7xl"
              data-testid="hero-headline"
            >
              If not you,<br />
              <span className="relative inline-block">
                then <span className="text-gold">WHO</span>.
              </span>
            </motion.h1>
            <motion.p
              initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.16, duration: 0.6 }}
              className="mt-4 font-display font-bold text-navy/80 text-xl lg:text-2xl leading-snug"
              data-testid="hero-taglline-voice"
            >
              A <span className="text-gold-deep">warm handoff</span> is a completed connection &mdash; not a referral.
            </motion.p>
            <motion.p
              initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.24, duration: 0.6 }}
              className="mt-6 text-lg lg:text-xl text-slateSoft leading-relaxed max-w-2xl"
              data-testid="hero-subheadline"
            >
              WHO is a coordination platform for veterans, families, and the organizations that
              serve them. A real
              <span className="text-navy-900 font-semibold"> WHO Guide</span>, supported by AI,
              coordinates each handoff &mdash; and stays with the person until the connection is
              made and the outcome is confirmed. The Pittsburgh pilot is now open to partners.
            </motion.p>

            <motion.div
              initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.35, duration: 0.6 }}
              className="mt-10 flex flex-wrap gap-4 items-center"
            >
              <Link
                to="/contact"
                data-testid="hero-request-demo-btn"
                className="inline-flex items-center gap-2 bg-gold hover:bg-gold-deep text-navy font-semibold px-6 py-3.5 rounded-md transition-colors"
              >
                Request a Demo <ArrowRight className="w-4 h-4" strokeWidth={2.5} />
              </Link>
              <Link
                to="/partners"
                data-testid="hero-join-network-btn"
                className="inline-flex items-center gap-2 border border-navy-800 text-navy-800 hover:bg-navy-800 hover:text-white font-semibold px-6 py-3.5 rounded-md transition-colors"
              >
                Join the Partner Network
              </Link>
              <Link
                to="/experience#try-it-live"
                data-testid="hero-meet-guides-link"
                className="group inline-flex items-center gap-1.5 text-navy-800 font-semibold hover:text-gold-deep transition-colors"
              >
                <span className="w-2 h-2 rounded-full bg-gold animate-pulse" aria-hidden />
                Try WHO Guides live
                <ArrowRight className="w-4 h-4 group-hover:translate-x-0.5 transition-transform" strokeWidth={2.5} />
              </Link>
            </motion.div>

            <div className="mt-14 grid grid-cols-3 max-w-md gap-6 border-t border-borderSubtle pt-6">
              <Stat label="Interface" value="Voice-first" />
              <Stat label="Human-in-the-loop" value="24/7" />
              <Stat label="Focus" value="The person" />
            </div>
          </div>

          <div className="lg:col-span-5 relative">
            <div className="relative rounded-lg overflow-hidden shadow-lg border border-borderSubtle">
              <img src={HERO_IMG} alt="Veteran embracing family" className="w-full h-[520px] object-cover" data-testid="hero-image" loading="lazy" />
              <div className="absolute inset-0 bg-gradient-to-t from-navy-900/85 via-navy-900/30 to-transparent" />
              <div className="absolute bottom-6 left-6 right-6 text-white">
                <p className="eyebrow text-gold">Warm handoff · verified</p>
                <p className="mt-2 font-display font-bold text-xl">The connection was made.</p>
              </div>
            </div>
            <div className="absolute -top-5 -left-5 w-24 h-24 rounded-lg bg-gold/15 border border-gold/40 hidden xl:block -z-10" aria-hidden />
            <div className="absolute -bottom-5 -right-5 w-20 h-20 rounded-full border-2 border-gold/60 hidden xl:block -z-10" aria-hidden />
          </div>
        </div>
      </section>

      {/* REFERENCED BY — federal research trust band */}
      <section className="py-14 bg-cream-panel border-b border-borderSubtle" data-testid="referenced-by-band">
        <div className="max-w-[1240px] mx-auto px-6 lg:px-10">
          <div className="flex flex-col md:flex-row items-start md:items-center gap-8">
            <div className="md:w-1/4">
              <p className="eyebrow text-gold-deep">Anchored in federal research</p>
              <p className="mt-2 text-navy font-display font-bold text-lg leading-tight">
                Every claim WHO makes is backed by public data.
              </p>
            </div>
            <div className="md:w-3/4 grid grid-cols-2 sm:grid-cols-4 gap-6 items-center">
              {[
                { name: "GAO", full: "U.S. Government Accountability Office", href: "https://www.gao.gov/products/gao-24-106248" },
                { name: "DoD TAP", full: "Transition Assistance Program", href: "https://www.dodtap.mil/" },
                { name: "VA", full: "Department of Veterans Affairs", href: "https://benefits.va.gov/transition/" },
                { name: "HUD", full: "AHAR · Veteran Homelessness", href: "https://www.huduser.gov/portal/datasets/ahar/2024-ahar-part-1-pit-estimates-of-homelessness-in-the-us.html" },
              ].map((r) => (
                <a
                  key={r.name}
                  href={r.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid={`ref-${r.name.toLowerCase().replace(/\W+/g, "-")}`}
                  className="group text-center px-2 py-3 rounded-md hover:bg-white transition-colors"
                >
                  <div className="font-display font-black text-navy text-2xl md:text-3xl tracking-tight group-hover:text-gold-deep transition-colors">
                    {r.name}
                  </div>
                  <div className="mt-1 text-[11px] uppercase tracking-widest text-slateSoft">
                    {r.full}
                  </div>
                </a>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CATEGORY POSITIONING — one-line declarative */}
      <section className="relative py-14 lg:py-16 bg-navy-deep text-cream border-t border-white/5" data-testid="home-category-band">
        <div className="max-w-[1240px] mx-auto px-6 lg:px-10 text-center">
          <motion.p
            initial={{ opacity: 0, y: 10 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="eyebrow text-gold"
          >
            Category
          </motion.p>
          <motion.h2
            initial={{ opacity: 0, y: 12 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.1, duration: 0.7 }}
            className="mt-4 font-display font-black text-cream tracking-tight leading-[1.05] text-3xl sm:text-4xl lg:text-5xl"
            data-testid="home-category-statement"
          >
            WHO is a <span className="text-gold">coordination infrastructure</span> company.
          </motion.h2>
          <motion.p
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ delay: 0.25, duration: 0.6 }}
            className="mt-4 text-cream/70 text-base lg:text-lg max-w-2xl mx-auto leading-relaxed"
          >
            Not a directory. Not a referral tool. Not another app. WHO is the connective tissue
            between the person, the organizations, and the outcome.
          </motion.p>
        </div>
      </section>

      {/* IMPACT NUMBERS — federal-verified statistics that quantify the gap */}
      <section className="py-16 lg:py-20 bg-white border-b border-borderSubtle" data-testid="home-impact-band">
        <div className="max-w-[1240px] mx-auto px-6 lg:px-10">
          <div className="max-w-2xl mb-10">
            <p className="eyebrow text-gold-deep">The scale · Federal data</p>
            <h2 className="mt-3 font-display font-black text-navy tracking-tight leading-[1.05] text-3xl lg:text-4xl">
              Programs exist. Coordination between them does not.
            </h2>
          </div>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              { v: "17.9M", l: "Living U.S. veterans", src: "VA · FY 2024" },
              { v: "~200K", l: "Service members transition each year", src: "GAO" },
              { v: "4,300+", l: "At-risk members with no warm handover", src: "GAO-24-106248" },
              { v: "32,882", l: "Veterans experiencing homelessness", src: "HUD · 2024 PIT" },
            ].map((s) => (
              <motion.div
                key={s.l}
                initial={{ opacity: 0, y: 12 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5 }}
                className="p-6 bg-cream-panel border border-borderSubtle rounded-lg hover:border-gold transition-colors"
                data-testid={`home-impact-stat-${s.l.toLowerCase().replace(/\W+/g, "-").slice(0, 30)}`}
              >
                <div className="font-display font-black text-gold-deep text-4xl lg:text-5xl leading-none tracking-tight">
                  {s.v}
                </div>
                <p className="mt-3 font-semibold text-navy text-sm leading-tight">{s.l}</p>
                <p className="mt-2 text-[10px] uppercase tracking-widest text-slateSoft">Source · {s.src}</p>
              </motion.div>
            ))}
          </div>
          <p className="mt-8 text-sm text-slateSoft">
            Full sourcing and methodology on <Link to="/problem" className="text-navy font-semibold underline decoration-borderSubtle hover:decoration-gold underline-offset-4">The Problem</Link>.
          </p>
        </div>
      </section>

      {/* THE CHALLENGE */}
      <Section eyebrow="The Challenge" title="Help exists. The systems around it don't talk to each other.">
        <div className="grid md:grid-cols-2 gap-10 items-start">
          <div>
            <p className="text-lg text-slateSoft leading-relaxed">
              Veterans, transitioning service members, military families, and community members face
              complex, disconnected systems across benefits, employment, healthcare, housing, education,
              food security, legal support, transportation, and mental wellness.
            </p>
            <p className="mt-5 text-lg text-slateSoft leading-relaxed">
              A referral is not a result. When one agency hands off to another, no one confirms whether
              the connection actually happened — and the person is often left to coordinate a system
              that was never designed for them.
            </p>
          </div>
          <div className="grid grid-cols-2 gap-3">
            {[
              { icon: <Landmark className="w-5 h-5" />, label: "Benefits" },
              { icon: <Briefcase className="w-5 h-5" />, label: "Employment" },
              { icon: <Heart className="w-5 h-5" />, label: "Healthcare" },
              { icon: <HomeIcon className="w-5 h-5" />, label: "Housing" },
              { icon: <GraduationCap className="w-5 h-5" />, label: "Education" },
              { icon: <Users className="w-5 h-5" />, label: "Family" },
              { icon: <Activity className="w-5 h-5" />, label: "Mental wellness" },
              { icon: <Building2 className="w-5 h-5" />, label: "Legal & Civic" },
            ].map((it) => (
              <div key={it.label} className="flex items-center gap-3 p-4 bg-white border border-borderSubtle rounded-md" data-testid={`challenge-tile-${it.label.toLowerCase()}`}>
                <span className="text-gold">{it.icon}</span>
                <span className="font-medium text-navy-900">{it.label}</span>
              </div>
            ))}
          </div>
        </div>
      </Section>

      {/* THE WHO SOLUTION */}
      <Section eyebrow="The WHO Solution" title="Coordination infrastructure — not another app." tone="navy">
        <div className="grid lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-6">
            <p className="text-lg text-white/85 leading-relaxed">
              WHO Guides&trade; &mdash; real people supported by nine specialist AI agents and backed
              by verified partners &mdash; help trusted organizations
              <span className="text-white font-semibold"> make, receive, track, and complete warm handoffs with accountability</span>.
              We coordinate around the person, so the person does not have to coordinate around us.
            </p>
            <ul className="mt-8 space-y-4 text-white/90">
              {[
                "Voice-first intake — a real WHO Guide handles the first conversation",
                "Nine specialist AI agents coordinate across every service domain",
                "Direct partner-to-partner warm handoffs with consent",
                "Closed-loop follow-up from initiation to confirmed outcome",
              ].map((li, i) => (
                <li key={i} className="flex gap-3" data-testid={`solution-bullet-${i}`}>
                  <span className="mt-1 w-2 h-2 rounded-full bg-gold shrink-0" />
                  <span className="leading-relaxed">{li}</span>
                </li>
              ))}
            </ul>
            <div className="mt-10 flex flex-wrap gap-4">
              <Link to="/solution" data-testid="solution-learn-more-btn" className="inline-flex items-center gap-2 bg-gold hover:bg-gold-deep text-navy font-semibold px-6 py-3.5 rounded-md transition-colors">
                See how WHO works <ArrowRight className="w-4 h-4" strokeWidth={2.5} />
              </Link>
            </div>
          </div>
          <div className="lg:col-span-6">
            <div className="relative rounded-lg overflow-hidden border border-white/10">
              <img src={COMMUNITY_IMG} alt="Diverse WHO partner leaders — a nurse, workforce navigator, veteran, community organizer, funder, and program directors — meeting around a table under the WHO Warm Hand Off logo, coordinating around the person they serve." className="w-full h-[420px] object-cover" loading="lazy" />
              <div className="absolute inset-0 bg-gradient-to-t from-navy-900/60 via-navy-900/15 to-transparent" />
            </div>
          </div>
        </div>
      </Section>

      {/* MID-PAGE HEADLINE — founder placement (moved out of the hero) */}
      <section className="py-14 lg:py-20 bg-navy-deep text-cream border-y border-white/5" data-testid="home-headline-band">
        <div className="max-w-[1240px] mx-auto px-6 lg:px-10 text-center">
          <h2 className="font-display font-black text-cream tracking-tight leading-[1.08] text-3xl sm:text-4xl lg:text-5xl" data-testid="home-headline-band-title">
            Strengthening the Connections That Support Veterans.
          </h2>
        </div>
      </section>

      {/* ECONOMICS OF CONNECTION — impact vision teaser */}
      <section className="py-16 lg:py-20 bg-cream-panel border-b border-borderSubtle" data-testid="home-economics-section">
        <div className="max-w-[1240px] mx-auto px-6 lg:px-10 grid lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-8">
            <p className="eyebrow text-gold-deep">The Economics of Connection</p>
            <h2 className="mt-3 font-display font-black text-navy tracking-tight leading-[1.05] text-3xl lg:text-4xl">
              Every Connection Matters. So Does Understanding Its Impact.
            </h2>
            <p className="mt-5 text-slateSoft leading-relaxed max-w-2xl">
              WHO is building more than a way to find services. We are developing a system to help veterans connect with meaningful support while helping communities better understand service gaps, coordination challenges, and opportunities to improve resource effectiveness.
            </p>
          </div>
          <div className="lg:col-span-4 lg:text-right">
            <Link to="/impact" data-testid="home-economics-cta" className="inline-flex items-center gap-2 bg-navy hover:bg-navy-deep text-cream font-semibold px-6 py-3.5 rounded-md transition-colors">
              Explore Our Impact Vision <ArrowRight className="w-4 h-4" strokeWidth={2.5} />
            </Link>
          </div>
        </div>
      </section>

      {/* VETERAN ENTREPRENEURSHIP — category highlight */}
      <section className="py-16 lg:py-20 bg-white border-b border-borderSubtle" data-testid="home-entrepreneurship-section">
        <div className="max-w-[1240px] mx-auto px-6 lg:px-10 grid lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-8">
            <p className="eyebrow text-gold-deep">Veteran Entrepreneurship &amp; Business Ownership</p>
            <h2 className="mt-3 font-display font-black text-navy tracking-tight leading-[1.05] text-3xl lg:text-4xl">
              Supporting Veterans Who Build Businesses
            </h2>
            <p className="mt-5 text-slateSoft leading-relaxed max-w-2xl">
              For many veterans, the next chapter of service and leadership begins with entrepreneurship.
              WHO helps veterans discover resources for business formation, training, mentorship, financing,
              certification, and growth — while developing a broader approach to meaningful service coordination.
            </p>
          </div>
          <div className="lg:col-span-4 lg:text-right">
            <Link to="/discovery?category=veteran_entrepreneurship" data-testid="home-entrepreneurship-cta" className="inline-flex items-center gap-2 bg-gold hover:bg-gold-deep text-navy font-semibold px-6 py-3.5 rounded-md transition-colors">
              Explore Veteran Entrepreneurship Resources <ArrowRight className="w-4 h-4" strokeWidth={2.5} />
            </Link>
          </div>
        </div>
      </section>

      {/* MEET WHO GUIDES — voice-native teaser */}
      <section
        id="who-guides"
        className="relative overflow-hidden py-24 lg:py-32 border-t border-borderSubtle bg-cream-panel"
        data-testid="who-guides-teaser-section"
      >
        {/* Ambient background */}
        <div className="absolute inset-0 pointer-events-none" aria-hidden>
          <div className="absolute -top-32 -right-32 w-[520px] h-[520px] rounded-full bg-gold/10 blur-3xl" />
          <div className="absolute -bottom-40 -left-40 w-[560px] h-[560px] rounded-full bg-navy/[0.06] blur-3xl" />
        </div>

        <div className="relative max-w-[1240px] mx-auto px-6 lg:px-10 grid lg:grid-cols-12 gap-14 items-center">
          <div className="lg:col-span-5">
            <p className="eyebrow text-gold-deep" data-testid="who-guides-teaser-eyebrow">
              Meet WHO Guides&trade;
            </p>
            <h2 className="mt-4 font-display font-black text-navy tracking-tight leading-[1.03] text-4xl sm:text-5xl lg:text-[3.4rem]">
              One conversation.<br />
              <span className="text-gold-deep">A team of specialists at work.</span>
            </h2>
            <p className="mt-6 text-lg text-slateSoft leading-relaxed">
              A person speaks one need. A real <span className="text-navy font-semibold">WHO Guide</span>,
              supported by nine specialist AI agents, coordinates the handoff &mdash; each agent
              responsible for its own domain, its own verified partners, and its own follow-through.
              A person stays accountable for the outcome. AI handles the coordination work.
            </p>

            {/* Specialist agent bar — visualizes the 9 agents at a glance */}
            <div className="mt-8 flex flex-wrap gap-1.5" data-testid="specialist-agent-bar">
              {[
                "Healthcare", "Benefits", "Housing", "Employment", "Education",
                "Transportation", "Family", "Community", "Financial",
              ].map((n) => (
                <span
                  key={n}
                  className="text-[10px] uppercase tracking-widest font-bold px-2.5 py-1.5 rounded-full bg-navy/[0.04] border border-navy/15 text-navy hover:bg-gold/15 hover:border-gold/50 hover:text-gold-deep transition-colors cursor-default"
                  data-testid={`specialist-chip-${n.toLowerCase()}`}
                >
                  {n} Guide&trade;
                </span>
              ))}
            </div>

            <ul className="mt-8 space-y-4">
              {[
                { t: "Voice-first intake, judgment-free", b: "Speak the way you actually think. No forms. No jargon. No triage tree." },
                { t: "Nine specialist agents, one orchestrator", b: "Each Guide owns a domain: appointments, applications, benefits, follow-up — all coordinated in real time." },
                { t: "Human-in-the-loop, always", b: "AI moves the process. A real WHO Guide stays with the person through every consequential moment." },
                { t: "Tracked to outcome, not activity", b: "Referrals aren&apos;t results. Every handoff is confirmed, every loop closed, every outcome measured." },
              ].map((f) => (
                <li key={f.t} className="flex gap-4" data-testid={`who-guides-teaser-feature-${f.t.toLowerCase().replace(/\W+/g, "-").slice(0, 40)}`}>
                  <span className="mt-2 w-2.5 h-2.5 rounded-full bg-gold shrink-0" />
                  <div>
                    <p className="font-display font-bold text-navy text-lg leading-tight">{f.t}</p>
                    <p className="mt-1 text-slateSoft leading-relaxed" dangerouslySetInnerHTML={{ __html: f.b }} />
                  </div>
                </li>
              ))}
            </ul>

            <div className="mt-10 flex flex-wrap gap-4">
              <Link
                to="/experience#try-it-live"
                data-testid="who-guides-teaser-see-full-btn"
                className="inline-flex items-center gap-2 bg-navy hover:bg-navy-deep text-cream font-semibold px-6 py-3.5 rounded-md transition-colors"
              >
                Try WHO Guides Live <ArrowRight className="w-4 h-4" strokeWidth={2.5} />
              </Link>
              <Link
                to="/contact"
                data-testid="who-guides-teaser-pilot-btn"
                className="inline-flex items-center gap-2 border border-navy/70 text-navy hover:bg-navy hover:text-cream font-semibold px-6 py-3.5 rounded-md transition-colors"
              >
                Join a Pilot
              </Link>
            </div>
          </div>

          <div className="lg:col-span-7">
            <WhoGuidesDemo variant="teaser" />
          </div>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <Section eyebrow="How it works" title="From need identified to connection confirmed.">
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
          {[
            { icon: <Compass className="w-6 h-6" />, step: "01", title: "Identify the need", body: "A person or partner surfaces a need in plain language, in context." },
            { icon: <MapPin className="w-6 h-6" />, step: "02", title: "Match with a trusted provider", body: "Verified, geo-located, eligibility-aware matches from the WHO network." },
            { icon: <Handshake className="w-6 h-6" />, step: "03", title: "Complete a warm handoff", body: "A secure, consent-based handoff between organizations — not a phone number." },
            { icon: <Target className="w-6 h-6" />, step: "04", title: "Track follow-up & outcomes", body: "Accepted, contacted, completed, or unresolved — everyone sees the truth." },
          ].map((s, i) => (
            <motion.div
              key={s.step}
              initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }} transition={{ delay: i * 0.08, duration: 0.5 }}
              className="bg-white border border-borderSubtle rounded-lg p-7 hover:shadow-md transition-shadow group"
              data-testid={`howitworks-step-${s.step}`}
            >
              <div className="flex items-center justify-between">
                <span className="text-gold">{s.icon}</span>
                <span className="font-display font-black text-navy-100 text-3xl group-hover:text-gold transition-colors">{s.step}</span>
              </div>
              <h3 className="mt-6 font-display font-bold text-navy-900 text-xl leading-tight">{s.title}</h3>
              <p className="mt-3 text-slateSoft leading-relaxed">{s.body}</p>
            </motion.div>
          ))}
        </div>
      </Section>

      {/* MOBILE APP SHOWCASE */}
      <Section eyebrow="The WHO App" title="Human-centered. Built for the moment help is needed." bg="bone">
        <div className="grid lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-5 order-2 lg:order-1">
            <p className="text-lg text-slateSoft leading-relaxed">
              A mobile companion that puts warm handoffs in the palm of the person who needs them.
              People aren&apos;t handed a list of numbers — they&apos;re introduced directly, followed up
              with, and never left alone in the gap.
            </p>
            <ul className="mt-8 space-y-5">
              {[
                { t: "Find Support Near You", b: "Geo-located, verified providers matched to the person's need and eligibility." },
                { t: "Talk to Someone", b: "Direct, secure connection to a real human at a trusted organization — not a phone tree." },
                { t: "Personalized Connections", b: "Trusted introductions to organizations, not a search page of unknown links." },
                { t: "Track Outcomes", b: "The person sees follow-up, next steps, and progress — because a referral isn't a result." },
                { t: "988 Crisis Support", b: "Immediate, always-visible access to crisis lifelines when it matters most." },
              ].map((f) => (
                <li key={f.t} className="flex gap-4" data-testid={`app-feature-${f.t.toLowerCase().replace(/\W+/g, "-")}`}>
                  <span className="mt-2 w-2.5 h-2.5 rounded-full bg-gold shrink-0" />
                  <div>
                    <p className="font-display font-bold text-navy-900 text-lg leading-tight">{f.t}</p>
                    <p className="mt-1 text-slateSoft leading-relaxed">{f.b}</p>
                  </div>
                </li>
              ))}
            </ul>
          </div>
          <div className="lg:col-span-7 order-1 lg:order-2 relative">
            <div className="relative flex justify-center">
              <div className="absolute inset-0 flex items-center justify-center pointer-events-none" aria-hidden>
                <div className="w-[440px] h-[440px] rounded-full bg-gold/10" />
              </div>
              <img
                src="/assets/who-app-mockup.png"
                alt="WHO mobile app showing warm handoff features"
                className="relative w-full max-w-[380px] h-auto drop-shadow-2xl"
                data-testid="app-mockup-image"
                loading="lazy"
              />
            </div>
            <div className="hidden lg:flex absolute top-16 -left-4 bg-white border border-borderSubtle rounded-md px-4 py-3 shadow-sm items-center gap-3" data-testid="mockup-callout-verified">
              <span className="w-2 h-2 rounded-full bg-gold" />
              <span className="text-sm font-semibold text-navy-900">Verified provider match</span>
            </div>
            <div className="hidden lg:flex absolute bottom-24 -right-4 bg-white border border-borderSubtle rounded-md px-4 py-3 shadow-sm items-center gap-3" data-testid="mockup-callout-handoff">
              <span className="w-2 h-2 rounded-full bg-gold" />
              <span className="text-sm font-semibold text-navy-900">Warm handoff · confirmed</span>
            </div>
          </div>
        </div>
      </Section>

      {/* WHO WE SERVE */}
      <Section eyebrow="Who WHO serves" title="Built for the people and the partners who serve them.">
        <div className="grid md:grid-cols-2 gap-10 items-center">
          <div className="grid grid-cols-2 gap-3">
            {[
              "Veterans", "Transitioning service members", "Military families",
              "Veteran service orgs", "Healthcare providers", "Workforce agencies",
              "Colleges & universities", "Public agencies", "Nonprofits",
              "Philanthropic & corporate partners",
            ].map((s) => (
              <div key={s} className="p-4 bg-white border border-borderSubtle rounded-md text-navy-900 font-medium" data-testid={`serve-tile-${s.toLowerCase().replace(/\W+/g, "-")}`}>
                {s}
              </div>
            ))}
          </div>
          <div className="relative">
            <div className="rounded-lg overflow-hidden border border-borderSubtle">
              <img src={MEETING_IMG} alt="Diverse community meeting" className="w-full h-[420px] object-cover" loading="lazy" />
            </div>
            <div className="absolute -bottom-6 left-6 right-6 bg-white border border-borderSubtle rounded-md p-5 shadow-md">
              <p className="eyebrow text-gold">Why WHO matters</p>
              <p className="mt-2 font-display font-bold text-navy-900 text-lg leading-snug">
                A referral is not a result. A warm handoff requires positive contact, accountability, follow-up, and trust.
              </p>
            </div>
          </div>
        </div>
      </Section>

      <ScanToConnect />

      {/* DATA PLATFORM — the compounding moat */}
      <section
        className="relative overflow-hidden py-24 lg:py-32 bg-navy-deep text-cream border-t border-white/5"
        data-testid="home-data-platform-section"
        id="data-platform"
      >
        <div className="absolute inset-0 pointer-events-none" aria-hidden>
          <div className="absolute -top-40 right-0 w-[560px] h-[560px] rounded-full bg-gold/12 blur-3xl" />
          <div className="absolute -bottom-40 left-0 w-[560px] h-[560px] rounded-full bg-gold/8 blur-3xl" />
          <div className="absolute inset-0 dot-grid opacity-[0.06]" />
        </div>

        <div className="relative max-w-[1240px] mx-auto px-6 lg:px-10">
          <div className="max-w-3xl">
            <p className="eyebrow text-gold" data-testid="data-platform-eyebrow">
              The compounding advantage
            </p>
            <h2 className="mt-4 font-display font-black text-cream tracking-tight leading-[1.03] text-4xl sm:text-5xl lg:text-[3.4rem]">
              WHO isn&apos;t just software.<br />
              <span className="text-gold">It&apos;s becoming a data platform.</span>
            </h2>
            <p className="mt-6 text-lg text-cream/80 leading-relaxed max-w-2xl">
              Every warm handoff generates signal that has never existed at population scale.
              As the network runs, WHO learns what actually works &mdash; and where the system
              breaks. Insights no directory, referral tool, or CRM can produce.
            </p>
          </div>

          {/* Insight tiles */}
          <div className="mt-14 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="data-platform-insights">
            {[
              { n: "01", t: "Which referrals fail most often",           b: "Not anecdote &mdash; live signal across categories, partners, and regions." },
              { n: "02", t: "Which organizations close the loop",         b: "The receiving partners who actually deliver, ranked by outcome." },
              { n: "03", t: "Average transition time",                    b: "From first spoken need to confirmed connection &mdash; measurable, comparable." },
              { n: "04", t: "Geographic service gaps",                    b: "Where veterans live vs. where services actually reach them." },
              { n: "05", t: "Where veterans drop out",                    b: "The exact step in the journey where the system loses them." },
              { n: "06", t: "Which interventions improve outcomes",       b: "Real-world A/B across coaching, timing, warm-handoff style, and follow-through cadence." },
              { n: "07", t: "Where bottlenecks occur",                    b: "Which stages, categories, and organizations slow the entire network down." },
              { n: "08", t: "Which populations are underserved",          b: "Rural vs. urban. New vs. long-out. LGBTQ+. Women. Families with children." },
              { n: "09", t: "Where policy actually moves outcomes",       b: "Correlate program changes with real handoff success at scale." },
            ].map((s, i) => (
              <motion.div
                key={s.t}
                initial={{ opacity: 0, y: 14 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.2 }}
                transition={{ delay: (i % 3) * 0.08, duration: 0.5 }}
                className="p-6 bg-white/[0.04] border border-white/10 hover:border-gold/40 rounded-lg transition-colors group"
                data-testid={`data-platform-insight-${s.n}`}
              >
                <div className="flex items-center justify-between">
                  <span className="font-display font-black text-gold text-xl leading-none">{s.n}</span>
                  <span className="w-1.5 h-1.5 rounded-full bg-gold group-hover:animate-pulse" aria-hidden />
                </div>
                <p className="mt-4 font-display font-bold text-cream text-base leading-tight">{s.t}</p>
                <p className="mt-2 text-cream/65 text-sm leading-relaxed" dangerouslySetInnerHTML={{ __html: s.b }} />
              </motion.div>
            ))}
          </div>

          {/* Value + governance framing */}
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.2 }}
            transition={{ duration: 0.6 }}
            className="mt-14 max-w-3xl p-8 lg:p-10 rounded-2xl bg-gradient-to-br from-white/[0.06] to-white/[0.02] border border-gold/25"
            data-testid="data-platform-value-callout"
          >
            <p className="eyebrow text-gold-light">Why this matters</p>
            <p className="mt-4 text-cream/90 text-lg leading-relaxed">
              That data becomes <span className="text-cream font-semibold">incredibly valuable</span>
              &mdash; not to exploit individuals, but to <span className="text-cream font-semibold">help
              organizations improve systems</span>. With strong privacy protections and appropriate
              governance, aggregated insights could inform <span className="text-gold font-semibold">policy</span>,
              <span className="text-gold font-semibold"> funding</span>, and
              <span className="text-gold font-semibold"> operational decisions</span>.
            </p>
          </motion.div>

          {/* Bottom callout — who this matters to */}
          <div className="mt-14 grid md:grid-cols-3 gap-4">
            {[
              { t: "Public agencies", b: "See what works. Fund what does." },
              { t: "Funders & philanthropies", b: "Portfolio outcomes, not activity reports." },
              { t: "Research & policy partners", b: "First-of-its-kind population-scale warm-handoff dataset." },
            ].map((r) => (
              <div
                key={r.t}
                className="p-5 rounded-md bg-gold/8 border border-gold/25"
                data-testid={`data-platform-audience-${r.t.toLowerCase().replace(/\W+/g, "-")}`}
              >
                <p className="text-[10px] uppercase tracking-widest text-gold font-bold">{r.t}</p>
                <p className="mt-1.5 text-cream/85 text-sm leading-relaxed">{r.b}</p>
              </div>
            ))}
          </div>

          <p className="mt-10 text-sm text-cream/50 max-w-3xl leading-relaxed">
            All insights are generated in a consent-first, privacy-respecting, IRB-aligned way.
            Individuals are never identified in aggregate reporting. See <Link to="/partners#research-innovation" className="text-gold hover:text-gold-light underline underline-offset-4 decoration-gold/40 hover:decoration-gold">Research &amp; Innovation Partners</Link> for the governance model.
          </p>
        </div>
      </section>

      <Testimonials />

      <PartnerAppreciation />

      <CtaBanner
        eyebrow="The help exists."
        title="WHO makes the connection accountable."
        primaryText="Become a Partner"
        primaryTo="/partners"
        secondaryText="Meet WHO Guides"
        secondaryTo="/platform"
      />
    </div>
  );
}

function Stat({ value, label }) {
  return (
    <div data-testid={`stat-${label.replace(/\W+/g, "-").toLowerCase()}`}>
      <div className="font-display font-black text-navy-900 text-2xl">{value}</div>
      <div className="mt-1 text-xs uppercase tracking-widest text-slateSoft">{label}</div>
    </div>
  );
}

export function Section({ eyebrow, title, children, tone = "light", bg = "white", id }) {
  const dark = tone === "navy";
  const bgClass = dark
    ? "bg-navy-900 text-white"
    : bg === "cream" || bg === "bone"
      ? "bg-cream"
      : bg === "panel"
        ? "bg-cream-panel"
        : "bg-white";
  return (
    <section id={id} className={`py-24 lg:py-32 border-t border-borderSubtle ${bgClass}`}>
      <div className="max-w-[1240px] mx-auto px-6 lg:px-10">
        <div className="max-w-3xl mb-14">
          {eyebrow && <p className={`eyebrow ${dark ? "text-gold" : "text-gold"}`}>{eyebrow}</p>}
          {title && (
            <h2 className={`mt-4 font-display font-black tracking-tight leading-[1.05] text-3xl sm:text-4xl lg:text-5xl ${dark ? "text-white" : "text-navy-900"}`}>
              {title}
            </h2>
          )}
        </div>
        {children}
      </div>
    </section>
  );
}
